import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMessage } from "../api/client";
import type { Category, Favorite, Preferences, Preferred, Trait, Unit } from "../api/types";
import { Card, HangTag, toast } from "../components/Bits";
import { HeightFields } from "../components/HeightFields";
import { SignInButtons } from "../components/SignInButtons";
import { ALTER_CHOICES, prefChoices, toggleAlter } from "../lib/prefs";
import { cmToFtIn, formatHeight, parseHeight } from "../lib/units";
import { useApp } from "../state";

const PROVIDER_NAME: Record<string, string> = { google: "Google", facebook: "Facebook", dev: "Dev sign-in" };

function YouCard() {
  const { me, setMe } = useApp();
  const u = me.user;
  const init = u.height_cm != null ? cmToFtIn(u.height_cm) : null;
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(u.name ?? "");
  const [unit, setUnit] = useState<Unit>(u.unit);
  const [a, setA] = useState(u.height_cm == null ? "" : u.unit === "cm" ? String(Math.round(u.height_cm)) : String(init!.ft));
  const [b, setB] = useState(init && u.unit !== "cm" ? String(init.inch) : "");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const typed = a.trim() || b.trim();
    const cm = typed ? parseHeight(unit, a, b) : null;
    if (typed && cm == null) {
      setErr(unit === "cm" ? "Type your height in centimeters, like 168." : "Type your height in feet and inches, like 5 and 6.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      setMe(await api.putProfile({ name: name.trim(), unit, ...(typed ? { height_cm: cm } : {}) }));
      setEditing(false);
      toast("Saved");
    } catch (x) {
      setErr(errorMessage(x));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card tag="You" mark="★">
      {!editing ? (
        <>
          <ul className="learn">
            <li>
              <b>Name</b>
              <span>{u.name || <span className="muted">Not set</span>}</span>
            </li>
            <li>
              <b>Height</b>
              <span>{formatHeight(u.height_cm, u.unit)}</span>
            </li>
            <li>
              <b>Units</b>
              <span>{u.unit === "cm" ? "Centimeters" : "Inches"}</span>
            </li>
          </ul>
          <button className="linkish" type="button" onClick={() => setEditing(true)}>
            Edit
          </button>
        </>
      ) : (
        <form className="stack" onSubmit={save} noValidate>
          <label className="eyebrow" htmlFor="p-name">
            What should we call you?
          </label>
          <div className="seam">
            <input id="p-name" autoComplete="given-name" placeholder="First name" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
          </div>
          <span className="eyebrow">Height</span>
          <HeightFields unit={unit} a={a} b={b} onUnit={setUnit} onA={setA} onB={setB} />
          {err && <p className="hint warn">{err}</p>}
          <div className="row">
            <button className="btn primary" type="submit" disabled={busy}>
              Save
            </button>
            <button className="btn ghost" type="button" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </Card>
  );
}

function PrefsCard() {
  const { meta } = useApp();
  const [prefs, setPrefs] = useState<Preferences | null>(null);
  const [err, setErr] = useState("");
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    api.preferences().then(setPrefs, (e) => setErr(errorMessage(e)));
  }, []);
  const groups = useMemo(() => {
    const asked = new Set((prefs?.items ?? []).map((i) => i.fit_point));
    return (Object.keys(meta.categories) as Category[])
      .map((c) => ({ cat: c, name: meta.categories[c].name, fps: meta.categories[c].fit_points.filter((f) => f.onboarding || asked.has(f.id)) }))
      .filter((g) => g.fps.length);
  }, [meta, prefs]);
  if (!prefs) return err ? <p className="hint warn">{err}</p> : null;

  const general = (fp: string) => prefs.items.find((i) => i.fit_point === fp && i.cut_group === null)?.preferred;
  /** Pick a preference; tapping the picked one again clears it. */
  const set = (fp: string, v: Preferred) => {
    setDirty(true);
    setPrefs((p) => {
      if (!p) return p;
      const rest = p.items.filter((i) => !(i.fit_point === fp && i.cut_group === null));
      return { ...p, items: general(fp) === v ? rest : [...rest, { fit_point: fp, cut_group: null, preferred: v }] };
    });
  };
  const save = async () => {
    try {
      setPrefs(await api.putPreferences(prefs));
      setDirty(false);
      toast("Preferences saved");
    } catch (e) {
      setErr(errorMessage(e));
    }
  };
  const cutCount = prefs.items.filter((i) => i.cut_group !== null).length;
  return (
    <Card tag="How you like things to fit" mark="♡">
      {groups.map((g) => (
        <div key={g.cat} className="stack">
          <h3>{g.name.charAt(0).toUpperCase() + g.name.slice(1)}</h3>
          <div className="qrows">
            {g.fps.map((f) => (
              <div key={f.id} className={"qrow" + (general(f.id) ? " answered" : "")}>
                <h4>
                  <span className="ck" aria-hidden="true" />
                  {f.onboarding?.question ?? f.label}
                </h4>
                <div className="row" role="group" aria-label={f.onboarding?.question ?? f.label}>
                  {prefChoices(f).map(([v, label]) => (
                    <button key={v} type="button" className="chip sm" aria-pressed={general(f.id) === v} onClick={() => set(f.id, v)}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <h3>Tailoring</h3>
      <div className="row">
        {ALTER_CHOICES.map(([k, label]) => (
          <button
            key={k}
            type="button"
            className="chip sm"
            aria-pressed={prefs.alter[k]}
            onClick={() => {
              setDirty(true);
              setPrefs((p) => p && { ...p, alter: toggleAlter(p.alter, k) });
            }}
          >
            {label}
          </button>
        ))}
      </div>
      {cutCount > 0 && <p className="small muted">Plus {cutCount} preference{cutCount > 1 ? "s" : ""} for specific cuts, learned from your reports.</p>}
      {err && <p className="hint warn">{err}</p>}
      <button className="btn primary" type="button" disabled={!dirty} onClick={() => void save()}>
        Save preferences
      </button>
    </Card>
  );
}

function TraitsCard() {
  const [traits, setTraits] = useState<Trait[] | null>(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.traits().then((x) => setTraits(x.traits), (e) => setErr(errorMessage(e)));
  }, []);
  const remove = async (code: string) => {
    try {
      await api.deleteTrait(code);
      setTraits((t) => (t ?? []).filter((x) => x.code !== code));
      toast("Removed. We won't learn it again.");
    } catch (e) {
      setErr(errorMessage(e));
    }
  };
  return (
    <Card tag="What we've learned" mark="✱">
      {err && <p className="hint warn">{err}</p>}
      {traits && !traits.length && <p className="aside">Nothing yet. Check an item, then tell us what happened with it.</p>}
      {traits && traits.length > 0 && (
        <div>
          {traits.map((t) => (
            <div className="histrow" key={t.code}>
              <span className="t">{t.label}</span>
              <button className="chip sm lite" type="button" onClick={() => void remove(t.code)} aria-label={`Remove: ${t.label}`}>
                Remove
              </button>
              <span className="s">
                {t.explanation}{" "}
                <span className="num">
                  · {t.evidence_count} report{t.evidence_count === 1 ? "" : "s"}
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function FavoritesCard() {
  const [favs, setFavs] = useState<Favorite[] | null>(null);
  useEffect(() => {
    api.favorites().then((x) => setFavs(x.favorites), () => setFavs([]));
  }, []);
  if (!favs || !favs.length) return null;
  const remove = async (id: string) => {
    try {
      await api.deleteFavorite(id);
      setFavs((f) => (f ?? []).filter((x) => x.id !== id));
    } catch (e) {
      toast(errorMessage(e));
    }
  };
  return (
    <Card tag="Pieces that fit you well" mark={favs.length}>
      <div className="stack">
        {favs.map((f) => (
          <div key={f.id} className="stack" style={{ gap: 4 }}>
            <HangTag p={f.product} cat={f.product.category} size={f.size_label + (f.variant && !/regular/i.test(f.variant) ? " " + f.variant : "")} />
            <button className="linkish" type="button" onClick={() => void remove(f.id)}>
              Remove
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function DataCard() {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const download = async () => {
    setErr("");
    try {
      const data = await api.exportData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `drape-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setErr(errorMessage(e));
    }
  };
  const del = async () => {
    setBusy(true);
    try {
      await api.deleteMe();
      window.location.assign("/");
    } catch (e) {
      setErr(errorMessage(e));
      setBusy(false);
    }
  };
  const signOut = async () => {
    try {
      await api.logout();
    } catch {
      /* reload anyway */
    }
    window.location.assign("/");
  };
  return (
    <Card tag="Your saved data" mark="{}">
      <p className="small">The structured record behind your notes: each item you checked, what we predicted, and how it actually fit. No brand ever sees it.</p>
      <div className="row">
        <button className="chip sm" type="button" onClick={() => void download()}>
          Download my data
        </button>
      </div>
      {confirm ? (
        <div className="banner bad" role="alert">
          <b>Delete your account?</b>
          <p className="small">Your sign-in, profile, favorites and checks are removed. Fit reports are kept only without anything that points to you. This can't be undone.</p>
          <div className="row">
            <button className="btn danger" type="button" disabled={busy} onClick={() => void del()}>
              Yes, delete everything
            </button>
            <button className="btn ghost" type="button" onClick={() => setConfirm(false)}>
              Keep my account
            </button>
          </div>
        </div>
      ) : null}
      {err && <p className="hint warn">{err}</p>}
      <div className="row" style={{ justifyContent: "space-between" }}>
        {!confirm && (
          <button className="btn ghost" type="button" onClick={() => setConfirm(true)}>
            Delete my account
          </button>
        )}
        <button className="btn ghost" type="button" onClick={() => void signOut()}>
          Sign out
        </button>
      </div>
    </Card>
  );
}

export function Profile() {
  const { me } = useApp();
  const nav = useNavigate();
  const linked = me.user.providers;
  return (
    <section className="view enter">
      <h1>
        <span className="ph">Your fit</span> <span className="ph"><em>profile</em></span>
      </h1>
      <p className="aside">
        Built only from what you've told us. No brand ever sees it, and <mark>every report makes it sharper.</mark>
      </p>
      {me.user.is_guest && (
        <Card tag="You're a guest" mark="!">
          <p className="aside">
            <b>Sign in so your next notes get sharper.</b> Everything you've done as a guest comes with you.
          </p>
          <SignInButtons next="/profile" />
        </Card>
      )}
      <YouCard />
      {!me.user.is_guest && (
        <Card tag="Sign-in" mark="✓" done>
          <p className="small">{linked.length ? "Signed in with " + linked.map((p) => PROVIDER_NAME[p] ?? p).join(" and ") + "." : "Signed in."}</p>
        </Card>
      )}
      <TraitsCard />
      <PrefsCard />
      <FavoritesCard />
      <div className="nudge">
        <p className="small">Every item you've checked, and the ones waiting on a fit report.</p>
        <button className="chip sm" type="button" onClick={() => nav("/history")}>
          History
        </button>
      </div>
      <DataCard />
    </section>
  );
}
